import { useRestApiProvider } from "@jugl-web/rest-api";
import { TicketListItemModel } from "@jugl-web/rest-api/tickets/models/TicketListItem";
import { WithOptimistic } from "@jugl-web/utils";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { buildGetTicketsQueryParams } from "../../utils/buildGetTicketsQueryParams";
import { useTicketListCache } from "./useTicketListCache";
import { UseTicketListOptions } from "./types";

const STALE_WHILE_REVALIDATE = true;

const DEFAULT_PAGE_SIZE = 20;

/**
 * Hook that handles ticket data fetching, infinite-scroll pagination, caching,
 * and exposes `setTickets` for optimistic local updates.
 */
export const useTicketList = ({
  entityId,
  filter,
  meId,
  contactId,
  searchQuery = "",
  sort = "desc",
  pageSize = DEFAULT_PAGE_SIZE,
  skip = false,
}: UseTicketListOptions) => {
  const { ticketsApi } = useRestApiProvider();
  const [fetchTickets] = ticketsApi.useLazyGetTicketsQuery();

  // Serialize all query dimensions so filter-change detection is cheap.
  const serializedFilter = useMemo(() => filter ?? "", [filter]);
  const serializedMeId = useMemo(() => meId ?? "", [meId]);
  const serializedContactId = useMemo(() => contactId ?? "", [contactId]);

  const { getCachedTickets, setCachedTickets } = useTicketListCache({
    entityId,
    filter: serializedFilter,
    meId: serializedMeId,
    contactId: serializedContactId,
    searchQuery,
    sort,
    pageSize: String(pageSize),
  });

  const [combinedTickets, setCombinedTickets] = useState<
    WithOptimistic<TicketListItemModel>[]
  >(() => (skip ? [] : (getCachedTickets() ?? [])));

  const [currentPage, setCurrentPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [isFetching, setIsFetching] = useState(false);
  const [isError, setIsError] = useState(false);
  const [fulfilledTimestamp, setFulfilledTimestamp] = useState<number>();

  /**
   * Track previous filter values so the layout-effect can detect when to
   * reset pagination vs. just fetching the next page.
   */
  const previousFiltersRef = useRef({
    entityId,
    serializedFilter,
    serializedMeId,
    serializedContactId,
    searchQuery,
    sort,
    skip,
  });

  const isInitializedRef = useRef(false);

  const fetchPage = useCallback(
    async (page: number): Promise<void> => {
      if (skip) {
        return;
      }

      setIsFetching(true);
      setIsError(false);

      const queryParams = buildGetTicketsQueryParams({
        filter,
        meId,
        contactId,
        searchQuery,
        sort,
      });

      const response = await fetchTickets(
        {
          entityId,
          ...queryParams,
          page,
          page_size: pageSize,
        },
        false
      );

      setIsFetching(false);

      if (!response?.data) {
        setIsError(true);
        return;
      }

      const responseData = response.data;

      setTotalCount(responseData.total_entries);
      setHasMore(responseData.total_pages > page);
      setFulfilledTimestamp(Date.now());

      if (page === 1) {
        // Replace the entire list on a fresh fetch.
        setCombinedTickets(responseData.data);
      } else {
        // Merge: update existing items that appear in the new page, then append new ones.
        setCombinedTickets((prev) => {
          const newDataMap = new Map(
            responseData.data.map((ticket) => [ticket.id, ticket])
          );

          const updatedExisting = prev.map(
            (ticket) => newDataMap.get(ticket.id) ?? ticket
          );

          const existingIds = new Set(prev.map((ticket) => ticket.id));
          const newTickets = responseData.data.filter(
            (ticket) => !existingIds.has(ticket.id)
          );

          return [...updatedExisting, ...newTickets];
        });
      }
    },
    [
      skip,
      filter,
      meId,
      contactId,
      searchQuery,
      sort,
      fetchTickets,
      entityId,
      pageSize,
    ]
  );

  const loadMore = useCallback(() => {
    if (!isFetching && hasMore) {
      setCurrentPage((prev) => prev + 1);
    }
  }, [isFetching, hasMore]);

  /** Re-fetch the current page without resetting pagination. */
  const refetchCurrentPage = useCallback(async () => {
    await fetchPage(currentPage);
  }, [currentPage, fetchPage]);

  /** Reset to page 1 and re-fetch from scratch. */
  const reset = useCallback(async () => {
    setCurrentPage(1);
    if (!STALE_WHILE_REVALIDATE) setCombinedTickets([]);
    setTotalCount(0);
    setHasMore(false);
    await fetchPage(1);
  }, [fetchPage]);

  /** Retry after an error on the last attempted page. */
  const retry = useCallback(async () => {
    await fetchPage(currentPage);
  }, [currentPage, fetchPage]);

  // Detect filter / skip changes and reset, or fetch the next page when only
  // the page counter advances.
  useLayoutEffect(() => {
    const prev = previousFiltersRef.current;

    const filtersChanged =
      prev.entityId !== entityId ||
      prev.serializedFilter !== serializedFilter ||
      prev.serializedMeId !== serializedMeId ||
      prev.serializedContactId !== serializedContactId ||
      prev.searchQuery !== searchQuery ||
      prev.sort !== sort ||
      prev.skip !== skip;

    if (filtersChanged) {
      previousFiltersRef.current = {
        entityId,
        serializedFilter,
        serializedMeId,
        serializedContactId,
        searchQuery,
        sort,
        skip,
      };

      setCurrentPage(1);
      if (!STALE_WHILE_REVALIDATE) setCombinedTickets([]);
      setTotalCount(0);
      setHasMore(false);
      setIsError(false);

      if (!skip) {
        fetchPage(1);
      } else {
        setCombinedTickets([]);
      }
    } else if (currentPage > 1) {
      fetchPage(currentPage);
    } else if (!isInitializedRef.current) {
      isInitializedRef.current = true;
      if (!skip) {
        fetchPage(currentPage);
      } else {
        setCombinedTickets([]);
      }
    }
  }, [
    entityId,
    serializedFilter,
    serializedMeId,
    serializedContactId,
    searchQuery,
    sort,
    skip,
    currentPage,
    fetchPage,
  ]);

  // Keep the in-memory cache in sync while actively fetching.
  useEffect(() => {
    if (!skip) {
      setCachedTickets(combinedTickets);
    }
  }, [combinedTickets, setCachedTickets, skip]);

  const ticketIds = useMemo(
    () => new Set(combinedTickets.map((t) => t.id)),
    [combinedTickets]
  );

  const isLoading =
    isFetching && combinedTickets.length === 0 && currentPage === 1;
  const isLoadingMore = isFetching && currentPage > 1;
  const isLoadingMoreError = isError && currentPage > 1;

  return {
    tickets: combinedTickets,
    ticketIds,
    isLoading,
    isLoadingMore,
    isFetching,
    isError,
    isLoadingMoreError,
    hasMore,
    fulfilledTimestamp,
    totalCount,
    currentPage,
    setTickets: setCombinedTickets,
    setTotalCount,
    loadMore,
    reset,
    retry,
    refetchCurrentPage,
  };
};
